import * as d3 from 'd3';
import type { BaseType } from 'd3';
import { isEmpty, merge } from 'lodash';

export type CategoryResponse = { response?: { rawScore: number } };

export type BuilderData = {
  mean: { class: number; reference?: number };
  criticalScore?: number;
  typicalRange?: { min: number; max: number };
  responses: CategoryResponse[];
  numberOfQuestions: number;
  onSelectScore: (id: number | null) => void;
};

type ChartOptions = {
  container: HTMLElement;
  width: number;
  height: number;
};

export type BuilderProps = { data: BuilderData; options: ChartOptions };

export const textBackground = (
  node: SVGGElement,
  options?: { xPad?: number; yPad?: number }
): void => {
  const { xPad = 0, yPad = 0 } = options || {};
  const box = node.getBBox();
  d3.select(node.parentNode as BaseType)
    .insert('rect', ':first-child')
    .attr('class', 'background')
    .attr('x', box.x - xPad)
    .attr('y', box.y - yPad)
    .attr('width', box.width + xPad * 2)
    .attr('height', box.height + yPad * 2)
    .attr('fill', 'white');
};

const rangeMarker = ({
  selection,
  label,
  xScale,
  start,
  end,
  offset = 0,
  height = 44
}): unknown => {
  const range = selection;
  const lines = range.append('g').attr('class', 'line-holder');
  const text = range.append('g').attr('class', 'label-holder');
  lines
    .append('line')
    .attr('y1', offset + height)
    .attr('y2', offset + height)
    .attr('x1', xScale(start))
    .attr('x2', xScale(end) + xScale.bandwidth())
    .attr('stroke-width', 2)
    .attr('stroke', 'black');

  lines
    .append('line')
    .attr('y1', offset + height)
    .attr('y2', offset)
    .attr('x1', xScale(start))
    .attr('x2', xScale(start))
    .attr('stroke-width', 2)
    .attr('stroke', 'black');

  lines
    .append('line')
    .attr('y1', offset + height)
    .attr('y2', offset)
    .attr('x1', xScale(end) + xScale.bandwidth())
    .attr('x2', xScale(end) + xScale.bandwidth())
    .attr('stroke-width', 2)
    .attr('stroke', 'black');

  text
    .append('text')
    .text(label)
    .attr('y', offset + height - 6)
    .attr('x', (xScale(end) + xScale.bandwidth() + xScale(start)) / 2)
    .attr('text-anchor', 'middle')
    .call((localSelection): void => textBackground(localSelection.node()));

  return range.node();
};

const xAxisMarker = ({
  selection,
  label: { height: lHeight, text },
  xScale,
  x,
  offset = 0,
  height = 50
}): unknown => {
  const marker = selection
    .classed('x-marker', true)
    .attr('transform', `translate(${xScale(x)},${offset})`);

  const arrow = marker.append('g').attr('class', 'arrow');
  arrow
    .append('line')
    .attr('y1', height)
    .attr('y2', 0)
    .attr('stroke-width', '2px')
    .attr('stroke', 'black');
  arrow.append('path').attr('class', 'arrow-head').attr('d', d3.symbol().type(d3.symbolCircle));

  const label = marker.append('g').attr('class', 'label');

  // text
  label
    .append('text')
    .attr('class', 'text')
    .attr('text-anchor', 'middle')
    .text(text)
    .attr('y', height + lHeight / 2 + 6)
    // background rectangle
    .call((localSelection): void => textBackground(localSelection.node(), { xPad: 8, yPad: 4 }));

  return marker.node();
};

export class CategoryScoreBuilder {
  // Options
  private _options = {
    container: null as HTMLElement | null,
    size: {
      width: 800,
      height: 350
    },
    plotConfig: {
      margin: {
        top: 20,
        left: 80,
        right: 50,
        bottom: 100
      }
    },
    fontSize: 12
  };

  setOptions(options: Partial<ChartOptions>): CategoryScoreBuilder {
    const newOptions = merge({}, this._options, options);
    this._options = newOptions;
    return this;
  }

  // Data
  private _data: BuilderData = {
    mean: { class: 0, reference: 0 },
    criticalScore: 0,
    typicalRange: { min: 0, max: 0 },
    onSelectScore: () => null,
    responses: [],
    numberOfQuestions: 0
  };

  setData(data: Partial<BuilderData>): CategoryScoreBuilder {
    const newData = { ...this._data, ...data };
    this._data = newData;
    return this;
  }

  // Internal vars
  private _scales: {
    xBand: d3.ScaleBand<number>;
    xLinear: d3.ScaleLinear<number, number>;
    y: d3.ScaleLinear<number, number>;
  };

  private getPlotAreaTop = (): number => this._options.plotConfig.margin.top;

  private getPlotAreaRight = (): number =>
    this._options.size.width - this._options.plotConfig.margin.right;

  private getPlotAreaBottom = (): number =>
    this._options.size.height - this._options.plotConfig.margin.bottom;

  private getPlotAreaLeft = (): number => this._options.plotConfig.margin.left;

  private _canvasSVG: SVGSVGElement;

  static create(args: {
    data: Partial<BuilderData>;
    options: Partial<ChartOptions>;
  }): CategoryScoreBuilder {
    const builder = new CategoryScoreBuilder();
    builder.setData(args.data);
    builder.setOptions(args.options);
    return builder;
  }

  plot(): void {
    const { container } = this._options;
    const { responses } = this._data;

    if (!container) {
      throw new Error('Container element is missing');
    }

    try {
      if (isEmpty(responses)) {
        this.clearCanvas();
        return;
      }
      this.initCanvas();
      this.initScales();
      this.plotStatistics();
      this.plotXAxis();
      this.plotYAxis();
    } catch (e) {
      this.clearCanvas();
      d3.select(container)
        .append('h4')
        .classed('mx-3 text-danger', true)
        .text(`Error: ${e.message}`);

      throw e;
    }
  }

  private clearCanvas(): void {
    const { container } = this._options;
    d3.select(container).selectAll('*').remove();
  }

  private initCanvas(): void {
    const { size, container } = this._options;
    const { width, height } = size;

    // Clear canvas
    this.clearCanvas();

    // Create svg
    this._canvasSVG = d3
      .select(container)
      .append('svg')
      .attr('viewBox', `0 0 ${width} ${height}`)
      .style('background', 'white')
      .node();
  }

  private initScales(): void {
    const { numberOfQuestions } = this._data;

    const scaleXBand = d3
      .scaleBand<number>()
      .domain(d3.range(numberOfQuestions + 1))
      .range([this.getPlotAreaLeft(), this.getPlotAreaRight()]);

    const scaleXLinear = d3
      .scaleLinear<number>()
      .domain([-0.5, numberOfQuestions + 0.5])
      .range([this.getPlotAreaLeft(), this.getPlotAreaRight()]);

    const scaleY = d3
      .scaleLinear()
      .domain([0, 1])
      .nice()
      .range([this.getPlotAreaBottom(), this.getPlotAreaTop()]);

    this._scales = {
      xBand: scaleXBand,
      xLinear: scaleXLinear,
      y: scaleY
    };
  }

  private plotStatistics(): void {
    const { responses, numberOfQuestions, criticalScore, onSelectScore } = this._data;
    const {
      _scales: { xBand, y },
      _canvasSVG
    } = this;

    const bins = d3
      .bin<CategoryResponse, number>()
      .thresholds(numberOfQuestions)
      .value((d) => d.response?.rawScore || 0)(responses);

    // Bars
    const scoreGroup = d3
      .select(_canvasSVG)
      .append('g')
      .attr('class', 'score-bars')
      .selectAll('g.score-group')
      .data(bins)
      .join('g')
      .attr('class', 'score-group')
      .attr('transform', (d) => `translate(${xBand(d[0]?.response?.rawScore || 0)},0)`);

    // draw bars
    scoreGroup
      .append('rect')
      .attr('class', 'bar')
      .attr('width', xBand.bandwidth())
      .attr('y', y(0))
      .transition()
      .duration(500)
      .attr('y', (d) => y(d.length / responses.length))
      .attr('height', (d) => y(0) - y(d.length / responses.length));

    // label bar
    scoreGroup
      .append('text')
      .classed('label', true)
      .text((d) => d.length || '')
      .attr('x', xBand.bandwidth() / 2)
      .style('text-anchor', 'middle')
      .attr('y', y(0) - 10)
      .transition()
      .duration(500)
      .attr('y', (d) => y(d.length / responses.length) - 4);

    // eslint-disable-next-line func-names
    scoreGroup.on('click', function (event, d) {
      const selected = d3.select(this).classed('selected');
      scoreGroup.classed('selected', false);
      d3.select(this).classed('selected', !selected).raise();
      onSelectScore(selected ? null : d[0]?.response?.rawScore || 0);
    });

    // critical score line
    if (criticalScore) {
      d3.select(_canvasSVG)
        .append('line')
        .attr('class', 'critical-score')
        .attr('x1', xBand(criticalScore) + xBand.bandwidth())
        .attr('x2', xBand(criticalScore) + xBand.bandwidth())
        .attr('y1', this.getPlotAreaBottom())
        .attr('y2', this.getPlotAreaTop())
        .attr('stroke', 'black')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', 2);
    }
  }

  private plotXAxis(): void {
    const {
      _canvasSVG,
      _scales: { xBand, xLinear },
      _data: { mean, criticalScore, typicalRange, numberOfQuestions }
    } = this;

    const g = d3
      .select(_canvasSVG)
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0,${this.getPlotAreaBottom()})`);

    g.append('g')
      .classed('mean', true)
      .call((selection) =>
        xAxisMarker({
          selection,
          xScale: xLinear,
          x: mean.class,
          label: {
            text: `Mean: ${mean.class.toFixed(1)} ${
              mean.reference ? ` (${mean.reference.toFixed(1)})` : ''
            }`,
            height: 30
          }
        })
      );

    if (typicalRange?.min && typicalRange?.max) {
      g.append('g')
        .attr('class', 'typical-range')
        .call((selection) =>
          rangeMarker({
            selection,
            xScale: xBand,
            start: typicalRange?.min,
            end: typicalRange?.max,
            label: 'Typical Range'
          })
        );
    }

    if (criticalScore) {
      g.append('g')
        .attr('class', 'critical-range')
        .call((selection) =>
          rangeMarker({
            selection,
            xScale: xBand,
            start: 0,
            end: criticalScore,
            label: 'Critical Range'
          })
        );
    }

    g.append('g').attr('class', 'axis').call(d3.axisBottom(xBand));

    g.selectAll('.tick').each((_d, i, nodes) =>
      d3
        .select(nodes[i])
        .select('text')
        .call((selection) => textBackground(selection.node() as SVGGElement))
    );

    g.append('text')
      .attr('class', 'axis-label')
      .text(`Score /${numberOfQuestions}`)
      .attr('text-anchor', 'end')
      .attr('x', this.getPlotAreaRight())
      .attr('y', 65); // Relative to the x axis.
  }

  private plotYAxis(): void {
    const {
      _canvasSVG,
      _scales: { y }
    } = this;

    const g = d3
      .select(_canvasSVG)
      .append('g')
      .attr('class', 'y-axis')
      .attr('transform', `translate(${this.getPlotAreaLeft()},0)`);

    g.append('g')
      .attr('class', 'axis')
      .call(d3.axisLeft(y).tickFormat((value) => `${value.valueOf() * 100}%`));

    g.append('text')
      .attr('class', 'axis-label')
      .attr('text-anchor', 'end')
      .text('% of learners')
      .attr('y', -65) // Relative to the y axis.
      .attr('x', -(this.getPlotAreaBottom() - this.getPlotAreaTop()) / 2)
      .attr('transform', 'rotate(-90)');
  }
}
